import { NSABoxSecret, NSARouterMakerResult } from "namirasoft-account-react";
import { ProviderDelayUnit, ProviderLimitPerUnit, ProviderRow, ProviderType } from "namirasoft-email-sender";
import { NSBox, NSBoxBoolean, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import React, { createRef } from "react";
import { EmailSenderEntities } from "../EmailSenderEntities";
import { EmailSenderLayout } from "../EmailSenderLayout";
import { EmailSenderRouterProps } from "../EmailSenderRouterProps";

export interface ProviderEditPageProps extends NSARouterMakerResult, EmailSenderRouterProps
{
    isEdit: boolean;
}

interface ProviderEditPageState
{
    type: ProviderType;
}

export default class ProviderEditPage extends React.Component<ProviderEditPageProps, ProviderEditPageState> 
{
    private NSBoxString_Name = createRef<NSBoxString>();
    private NSBoxString_Region = createRef<NSBoxString>();
    private NSABoxSecret_AccessKey = createRef<NSABoxSecret>();
    private NSABoxSecret_SecretKey = createRef<NSABoxSecret>();
    private NSBoxString_From = createRef<NSBoxString>();
    private NSBoxEnum_Type = createRef<NSBoxEnum<ProviderType>>();
    private NSBoxString_Host = createRef<NSBoxString>();
    private NSBoxInteger_Port = createRef<NSBoxInteger>();
    private NSABoxSecret_Username = createRef<NSABoxSecret>();
    private NSABoxSecret_Password = createRef<NSABoxSecret>();
    private NSBoxBoolean_Limit = createRef<NSBoxBoolean>();
    private NSBoxInteger_Limit_Count = createRef<NSBoxInteger>();
    private NSBoxInteger_Limit_Per_Value = createRef<NSBoxInteger>();
    private NSBoxEnum_Limit_Per_Unit = createRef<NSBoxEnum<ProviderLimitPerUnit>>();
    private NSBoxBoolean_Delay = createRef<NSBoxBoolean>();
    private NSBoxInteger_Delay_Value = createRef<NSBoxInteger>();
    private NSBoxEnum_Delay_Unit = createRef<NSBoxEnum<ProviderDelayUnit>>();
    private NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: ProviderEditPageProps)
    {
        super(props);
        this.state = {
            type: ProviderType.Gmail
        };
    }
    override render()
    {
        let extra_tabs = [];
        extra_tabs.push(
            {
                title: "Limit",
                clicked: true,
                getContent: () =>
                {
                    return (
                        <NSColumn style={{ width: NSBox.width.double }}>
                            <NSRow>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Limit}
                                    title="Limit"
                                    required
                                    style={{ width: "100%" }}
                                    defaultValue={false}
                                    onChanged={() => this.setState({})}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-limit" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Limit_Count}
                                    title="Limit Count"
                                    required
                                    min={0}
                                    style={{ width: "100%" }}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-limit-count" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Limit.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Limit_Per_Value}
                                    title="Limit Per Value"
                                    required
                                    min={1}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-limit-per-value" }}
                                />
                                <NSBoxEnum<ProviderLimitPerUnit>
                                    ref={this.NSBoxEnum_Limit_Per_Unit}
                                    title="Limit Per Unit"
                                    required
                                    multiple={false}
                                    getEnumObject={() => ProviderLimitPerUnit}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-limit-per-unit" }}
                                />
                            </NSRow>
                        </NSColumn>
                    );
                }
            },
            {
                title: "Delay",
                clicked: true,
                getContent: () =>
                {
                    return (
                        <NSColumn style={{ width: NSBox.width.double }}>
                            <NSRow>
                                <NSBoxBoolean
                                    ref={this.NSBoxBoolean_Delay}
                                    title="Delay"
                                    required
                                    style={{ width: "100%" }}
                                    defaultValue={false}
                                    onChanged={() => this.setState({})}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-delay" }}
                                />
                            </NSRow>
                            <NSRow style={this.NSBoxBoolean_Delay.current?.getValueBool() ? {} : { display: "none" }}>
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Delay_Value}
                                    title="Delay Value"
                                    required
                                    min={1}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-delay-value" }}
                                />
                                <NSBoxEnum<ProviderDelayUnit>
                                    ref={this.NSBoxEnum_Delay_Unit}
                                    title="Delay Unit"
                                    required
                                    multiple={false}
                                    getEnumObject={() => ProviderDelayUnit}
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-delay-unit" }}
                                />
                            </NSRow>
                        </NSColumn>
                    );
                }
            }
        );

        return (
            <EmailSenderLayout<ProviderRow, ProviderRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to update the details of an existing Email provider." : "This section allows you to enter information to create a provider so you can send messages. If you choose Namirasoft as your provider type, manual messages will not be available and only automated system alerts will be sent. Click the info icon for more information about each field."
                }}
                pages={{
                    entity: EmailSenderEntities.provider(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ProviderType, ProviderType.Gmail) ?? ProviderType.Gmail;
                                        let meta = null;

                                        if (type === ProviderType.AWS)
                                        {
                                            let region = this.NSBoxString_Region.current?.getValue() ?? "";
                                            let access_key = this.NSABoxSecret_AccessKey.current?.getValue() ?? "";
                                            let secret_key = this.NSABoxSecret_SecretKey.current?.getValue() ?? "";
                                            let from = this.NSBoxString_From.current?.getValue() ?? "";
                                            meta = { region, access_key, secret_key, from };
                                        }
                                        else if (type === ProviderType.SMTP)
                                        {
                                            let host = this.NSBoxString_Host.current?.getValue() ?? "";
                                            let port = this.NSBoxInteger_Port.current?.getValue() ?? "";
                                            let username = this.NSABoxSecret_Username.current?.getValue() ?? "";
                                            let password = this.NSABoxSecret_Password.current?.getValue() ?? "";
                                            meta = {
                                                host, port, username, password
                                            };
                                        }
                                        else if (type === ProviderType.Gmail)
                                            meta = {};
                                        else if (type === ProviderType.Namirasoft)
                                            meta = {};

                                        // Limit
                                        let limit: boolean = this.NSBoxBoolean_Limit.current?.getValueBool() ?? false;
                                        let limit_count: number | null = null;
                                        let limit_per_value: number | null = null;
                                        let limit_per_unit: ProviderLimitPerUnit | null = null;
                                        if (limit)
                                        {
                                            limit_count = this.NSBoxInteger_Limit_Count.current?.getValue() ?? 0;
                                            limit_per_value = this.NSBoxInteger_Limit_Per_Value.current?.getValue() ?? 0;
                                            limit_per_unit = this.NSBoxEnum_Limit_Per_Unit.current?.getValueEnumOne(ProviderLimitPerUnit, ProviderLimitPerUnit.Second) ?? ProviderLimitPerUnit.Second;
                                        }

                                        // Delay
                                        let delay: boolean = this.NSBoxBoolean_Delay.current?.getValueBool() ?? false;
                                        let delay_value: number | null = null;
                                        let delay_unit: ProviderDelayUnit | null = null;
                                        if (delay)
                                        {
                                            delay_value = this.NSBoxInteger_Delay_Value.current?.getValue() ?? 0;
                                            delay_unit = this.NSBoxEnum_Delay_Unit.current?.getValueEnumOne(ProviderDelayUnit, ProviderDelayUnit.Second) ?? ProviderDelayUnit.Second;
                                        }

                                        let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        return {
                                            id: "",
                                            user_id: "",
                                            workspace_id: "",

                                            name,
                                            type,
                                            meta,
                                            e_verified: true,

                                            limit,
                                            limit_count,
                                            limit_per_value,
                                            limit_per_unit,

                                            delay,
                                            delay_value,
                                            delay_unit,

                                            description,

                                            created_at: new Date(),
                                            updated_at: new Date()
                                        }
                                    },
                                    setRow: (row) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxEnum_Type.current?.setValue(row.type);
                                        this.setState({ type: row.type }, () =>
                                        {
                                            if (row.type === ProviderType.AWS)
                                            {
                                                this.NSBoxString_Region.current?.setValue(row.meta.region);
                                                this.NSABoxSecret_AccessKey.current?.setValue(row.meta.access_key);
                                                this.NSABoxSecret_SecretKey.current?.setValue(row.meta.secret_key);
                                                this.NSBoxString_From.current?.setValue(row.meta.from);
                                            }
                                            else if (row.type === ProviderType.SMTP)
                                            {
                                                this.NSBoxString_Host.current?.setValue(row.meta.host);
                                                this.NSBoxInteger_Port.current?.setValue(row.meta.port);
                                                this.NSABoxSecret_Username.current?.setValue(row.meta.username);
                                                this.NSABoxSecret_Password.current?.setValue(row.meta.password);
                                            }
                                            else if (row.type === ProviderType.Gmail)
                                            {
                                            }
                                            else if (row.type === ProviderType.Namirasoft)
                                            {
                                            }
                                        });
                                        this.NSBoxBoolean_Limit.current?.setValue(row.limit);
                                        if (row.limit)
                                        {
                                            this.NSBoxInteger_Limit_Count.current?.setValue(row.limit_count);
                                            this.NSBoxInteger_Limit_Per_Value.current?.setValue(row.limit_per_value);
                                            this.NSBoxEnum_Limit_Per_Unit.current?.setValue(row.limit_per_unit);
                                        }
                                        this.NSBoxBoolean_Delay.current?.setValue(row.delay);
                                        if (row.delay)
                                        {
                                            this.NSBoxInteger_Delay_Value.current?.setValue(row.delay_value);
                                            this.NSBoxEnum_Delay_Unit.current?.setValue(row.delay_unit);
                                        }
                                        this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");
                                    },
                                },
                                ui: {
                                    extra_tabs
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            title="Name"
                            required
                            info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-name" }}
                        />
                        <NSBoxEnum<ProviderType>
                            ref={this.NSBoxEnum_Type}
                            title="Type"
                            multiple={false}
                            required
                            getEnumObject={() => ProviderType}
                            onChanged={() =>
                            {
                                let type = this.NSBoxEnum_Type.current?.getValueEnumOne(ProviderType, ProviderType.Gmail) ?? ProviderType.Gmail;
                                this.setState({ type });
                            }}
                            searchable={false}
                            info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-type" }}
                        />
                    </NSRow>
                    {
                        this.state.type === ProviderType.AWS &&
                        <>
                            <NSRow>
                                <NSBoxString
                                    ref={this.NSBoxString_Region}
                                    required
                                    title="Region"
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-region" }}
                                />
                                <NSBoxString
                                    ref={this.NSBoxString_From}
                                    required
                                    title="From"
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-from" }}
                                />
                            </NSRow>
                            <NSRow>
                                <NSABoxSecret
                                    ref={this.NSABoxSecret_AccessKey}
                                    {...this.props}
                                    required
                                    title="Access Key"
                                    auto_complete="new-password"
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-access-key" }}
                                />
                                <NSABoxSecret
                                    ref={this.NSABoxSecret_SecretKey}
                                    {...this.props}
                                    required
                                    title="Secret Key"
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-secret-key" }}
                                />
                            </NSRow>
                        </>
                    }
                    {
                        this.state.type === ProviderType.SMTP &&
                        <>
                            <NSRow>
                                <NSBoxString
                                    ref={this.NSBoxString_Host}
                                    required
                                    title='Host'
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-host" }}
                                />
                                <NSBoxInteger
                                    ref={this.NSBoxInteger_Port}
                                    required
                                    title='Port'
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-port" }}
                                />
                            </NSRow>
                            <NSRow>
                                <NSABoxSecret
                                    ref={this.NSABoxSecret_Username}
                                    {...this.props}
                                    required
                                    title='Username'
                                    auto_complete="new-password"
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-user" }}
                                />
                                <NSABoxSecret
                                    ref={this.NSABoxSecret_Password}
                                    {...this.props}
                                    required
                                    title='Password'
                                    info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-password" }}
                                />
                            </NSRow>
                        </>
                    }
                    <NSRow>
                        <NSBoxTextArea
                            ref={this.NSBoxTextArea_Description}
                            title="Description"
                            required={false}
                            rows={5}
                            style={{ width: "100%" }}
                            info={{ link: "https://namirasoft.com/email-sender/console-guide/provider/#field-description" }}
                        />
                    </NSRow>
                </NSColumn>
            </EmailSenderLayout >
        );
    }
}